import MDAlert from "components/MDAlert";
import { SelectOptions } from "components/Shared/CustomSelect/CustomSelect";
import {
  REFETCH_QUERY_DEFAULT_PAGE,
  REFETCH_QUERY_FIRST_VARIABLE,
} from "constants/RefetchVariables";
import { QueryGetProductsOrderByColumn, SortOrder } from "generated/graphql";
import useGetProducts from "hooks/products/useGetProducts";
import { usePaginatedVariables } from "hooks/strings/usePaginatedVariables";
import { capitalCase } from "change-case";
import parseGraphQLError from "utils/graphQL/parseGraphQLError";
import { useMemo, useEffect, memo } from "react";

function ProductOptions({
  children,
}: {
  children: ({
    productOptions,
    loading,
    search,
  }: {
    productOptions: SelectOptions;
    loading: boolean;
    search: (v: string) => void;
  }) => React.ReactNode;
}) {
  const { filtering } = usePaginatedVariables();

  const { loading, data, error, getAllData, paginatorInfo } = useGetProducts({
    first: REFETCH_QUERY_FIRST_VARIABLE,
    search: filtering.debouncedSearchTerm,
    page: REFETCH_QUERY_DEFAULT_PAGE,
    orderBy: [{ column: QueryGetProductsOrderByColumn.NAME, order: SortOrder.ASC }],
  });

  useEffect(() => {
    if (!loading && paginatorInfo?.total && (data?.length ?? 0) < paginatorInfo.total) {
      getAllData();
    }
  }, [data?.length, getAllData, loading, paginatorInfo?.total]);

  if (error) {
    return <MDAlert color="error">{parseGraphQLError(error)}</MDAlert>;
  }
  const products = data ?? [];

  const productOptions = useMemo(
    () =>
      products
        .map((product) => ({
          category: product?.organizationProductType.name
            ? capitalCase(product.organizationProductType.name)
            : "",
          label: product.subcontracted
            ? `[Subcontracted] ${product.name}`
            : product.unitPriceProduct
            ? `[Unit Priced] ${product.name}`
            : product.name,
          value: product.id,
          measurementJob: product.measurementJob,
          measurementMaterial: product.measurementMaterial,
          dailyUnitsComplete: product.dailyUnitsComplete,
        }))
        .sort((a, b) => (a?.category && b?.category ? a.category.localeCompare(b.category) : -1)),
    [products]
  );

  return <>{children({ productOptions, loading, search: filtering.setSearchTerm })}</>;
}

export default memo(ProductOptions);
